import * as t from "../../../../lib/backend/manufacturing2.generated.types";

import type { LineOrders } from "./linesOrders";
import type { PayloadAction } from "../../../redux/types";
import { deepClone } from "../../../../lib/Util";

export const UPDATE_RECOMMEND_PLAN = "update_recommend_plan";
export const UPDATE_INITIAL_RECOMMEND_PLAN = "update_initial_recommend_plan";
export const UPDATE_CURRENT_RECOMMEND_PLAN = "update_current_recommend_plan";
export const SET_PENDING_INITIAL_REPORT_UUID = "set_pending_initial_report_uuid";
export const SET_PENDING_CURRENT_REPORT_UUID = "set_pending_current_report_uuid";
export const UPDATE_REPORT_UUID = "update_report_uuid";

export type RecommendPlanParamsAndCriteria = {
    recommend_criteria_delays: number;
    recommend_criteria_intermediate_stock: number;
    recommend_criteria_tool_changes: number;
    recommend_param_beam_size: number;
    recommend_param_can_change_workorder_line: boolean;
    recommend_param_consider_alternative_lines?: boolean;
    recommend_param_consider_priority?: boolean;
    recommend_param_consider_people_presences?: boolean;
    recommend_param_consider_people_competences?: boolean;
    recommend_param_customer_priority: boolean;
    recommend_param_freeze_shifts: number;
    recommend_param_freeze_work_orders: boolean;
    recommend_param_include_plan_orders: boolean;
    recommend_param_include_unscheduled_orders: boolean;
    recommend_param_meta_optimization: string;
    recommend_param_need_score_factor: number;
    recommend_param_plan_after_frozen_orders: boolean;
    recommend_param_override_shifts?: number;
    recommend_param_setup_code_change_factor: number;
    recommend_param_time_horizont_shifts: number;
    recommend_param_time_horizont_weeks?: number;
    recommend_param_track_input_material: boolean;
    recommend_param_unscheduled_offset_shifts: number;
    recommend_param_use_productions_of_unscheduled_operations: boolean;
    recommend_param_use_requirements_from_unscheduled_operations: boolean;
    recommend_param_use_safety_stock: boolean;
    recommend_param_work_duration_factor: number;
    recommend_param_max_order_end_date?: number;
    recommend_param_horizon_limit?: number;
    recommend_planning_table_horizon_days?: number;
    recommend_param_alternatives_strategy?: "only-primary" | "all-lines-keep-prefered" | "all-lines-no-prefered";
    recommend_param_include_history?: boolean;
    recommend_param_consider_optimum_batch?: boolean;
    recommend_param_run_until_phase?: "1" | "2" | "3" | "4";
    recommend_strategy: string;
};

export type RunRecommendPlanParameters = {
    line_group_uuid: string;
    line_uuids: string[];
    lines_orders: LineOrders[];
    state: RecommendPlanParamsAndCriteria;
    planning_weeks: number;
    freeze_orders: t.IRunLinegroupPlanningFreezeOrder[];
};

export type RecommendPlan = {
    plan_uuid: string;
    parameters: RunRecommendPlanParameters;
    report_uuid?: string;
    plan_created_ts: number;
    score?: t.IPlanScore;
    stock_report_uuid?: string;
};

export type UpdateRecommendPlan = {
    plan_uuid: string;
    parameters: RunRecommendPlanParameters;
    plan_created_ts: number;
    score?: t.IPlanScore;
    stock_report_uuid?: string;
    report_uuid?: string;
};

export type RecommendPlanState = {
    recommend_plans: RecommendPlan[];
    current_recommend_plan?: RecommendPlan | null;
    initial_recommend_plan?: RecommendPlan | null;
    pending_current_report_uuid: string;
    pending_initial_report_uuid: string;
};

export type UpdateRecommendPlanAction = PayloadAction<typeof UPDATE_RECOMMEND_PLAN, UpdateRecommendPlan>;
export type UpdateInitialPlanAction = PayloadAction<typeof UPDATE_INITIAL_RECOMMEND_PLAN, UpdateRecommendPlan | null>;
export type UpdateCurrentPlanAction = PayloadAction<typeof UPDATE_CURRENT_RECOMMEND_PLAN, UpdateRecommendPlan | null>;
export type UpdateReportRecommendPlanAction = PayloadAction<typeof UPDATE_REPORT_UUID, UpdateReportRecommendPlan>;
export type SetPendingInitialReportUuidAction = PayloadAction<typeof SET_PENDING_INITIAL_REPORT_UUID, string>;
export type SetPendingCurrentReportUuidAction = PayloadAction<typeof SET_PENDING_CURRENT_REPORT_UUID, string>;
export type RecommendPlanActions =
    | UpdateRecommendPlanAction
    | UpdateInitialPlanAction
    | UpdateCurrentPlanAction
    | UpdateReportRecommendPlanAction
    | SetPendingInitialReportUuidAction
    | SetPendingCurrentReportUuidAction;

type UpdateReportRecommendPlan = {
    plan_uuid: string;
    report_uuid: string;
};

const recommend_plan_state = {
    recommend_plans: [],
    pending_current_report_uuid: "",
    pending_initial_report_uuid: "",
};

export const recommendPlanReducer = (state: RecommendPlanState, action: RecommendPlanActions): RecommendPlanState => {
    if (!state) {
        state = deepClone(recommend_plan_state);
    }

    if (action.type === UPDATE_RECOMMEND_PLAN) {
        const recommend_plans = state.recommend_plans;
        recommend_plans.push({
            plan_uuid: action.data.plan_uuid,
            parameters: action.data.parameters,
            plan_created_ts: action.data.plan_created_ts,
            score: action.data.score,
            stock_report_uuid: action.data.stock_report_uuid,
        });
        return { ...state, recommend_plans: [...recommend_plans] };
    } else if (action.type === UPDATE_REPORT_UUID) {
        const { plan_uuid, report_uuid } = action.data;
        const plan = state.recommend_plans.find(p => p.plan_uuid === plan_uuid);
        if (plan) {
            plan.report_uuid = report_uuid;
            return { ...state, recommend_plans: [...state.recommend_plans] };
        }
    } else if (action.type === UPDATE_INITIAL_RECOMMEND_PLAN) {
        return { ...state, initial_recommend_plan: action.data !== null ? { ...action.data } : null };
    } else if (action.type === UPDATE_CURRENT_RECOMMEND_PLAN) {
        return { ...state, current_recommend_plan: action.data !== null ? { ...action.data } : null };
    } else if (action.type === SET_PENDING_INITIAL_REPORT_UUID) {
        return { ...state, pending_initial_report_uuid: action.data };
    } else if (action.type === SET_PENDING_CURRENT_REPORT_UUID) {
        return { ...state, pending_current_report_uuid: action.data };
    }

    return state;
};
